(() => {
  if (window.RedgloveAutoGift) return;
  const configElement = document.getElementById('rg-auto-gift-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch (error) { return; }
  const source = 'rg-auto-gift';
  const root = String(config.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const messages = config.messages || {};
  const rules = (config.rules || []).filter(rule => rule.id && Number.isSafeInteger(rule.variantId) && Array.isArray(rule.productIds)).map(rule => ({
    ...rule, productIds: new Set(rule.productIds.map(Number))
  }));
  if (!rules.length) return;
  let requested = 0;
  let completed = 0;
  let running = null;
  let externalLocks = 0;
  let externalTail = Promise.resolve();
  let unsafe = Boolean(config.chargedGift);
  let notice = '';
  let lastCart = null;
  let retryTimer = null;
  let retryCount = 0;
  let mutationRevision = 0;
  const waiters = [];
  const blocked = new Map();
  const checkoutLocks = new Map();
  const isOwned = item => String(item.properties?._rg_auto_gift) === 'true';
  const itemRule = item => rules.find(rule => Number(item.variant_id || item.id) === rule.variantId);
  const cartFingerprint = cart => JSON.stringify({
    items: cart.items.filter(item => !isOwned(item)).map(item => [Number(item.product_id), Number(item.variant_id || item.id), Number(item.quantity), item.properties || {}]).sort((a, b) => a[1] - b[1]),
    discounts: cart.discount_codes || [], currency: cart.currency
  });
  const isEligible = (rule, cart) => cart.items.some(item => !isOwned(item) && Number(item.quantity) > 0 && rule.productIds.has(Number(item.product_id)));
  const checkoutSelector = 'button[name="checkout"],input[name="checkout"],a[href*="/checkout"],.shopify-payment-button,shopify-accelerated-checkout,shopify-accelerated-checkout-cart,.cart__dynamic-checkout-buttons,.additional-checkout-buttons';
  const locked = () => Boolean(running || externalLocks || unsafe || completed < requested);

  const renderNotice = () => {
    const containers = [document.querySelector('#CartDrawer .drawer__footer'), document.querySelector('#main-cart-footer .cart__blocks'), document.querySelector('#cart-notification .cart-notification__links')].filter(Boolean);
    for (const container of containers) {
      let element = container.querySelector(':scope > .rg-auto-gift-notice');
      if (!element) {
        element = document.createElement('div');
        element.className = 'rg-auto-gift-notice caption';
        element.setAttribute('role', 'status');
        element.setAttribute('aria-live', 'polite');
        const text = document.createElement('p');
        text.className = 'rg-auto-gift-notice__text';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'link button-label';
        retry.textContent = messages.retry || '';
        retry.addEventListener('click', () => { retryCount = 0; requestReconcile(); });
        element.append(text, retry);
        container.prepend(element);
      }
      element.hidden = !notice;
      element.querySelector('p').textContent = notice;
      element.querySelector('button').hidden = !unsafe;
    }
  };

  const updateCheckoutLocks = () => {
    if (locked()) {
      for (const element of document.querySelectorAll(checkoutSelector)) {
        if (!checkoutLocks.has(element)) checkoutLocks.set(element, { disabled: element.disabled, inert: element.inert, ariaDisabled: element.getAttribute('aria-disabled') });
        if ('disabled' in element) element.disabled = true;
        else element.inert = true;
        element.setAttribute('aria-disabled', 'true');
      }
    } else {
      for (const [element, previous] of checkoutLocks) {
        if ('disabled' in element) element.disabled = previous.disabled;
        else element.inert = previous.inert;
        if (previous.ariaDisabled === null) element.removeAttribute('aria-disabled');
        else element.setAttribute('aria-disabled', previous.ariaDisabled);
      }
      checkoutLocks.clear();
    }
    renderNotice();
  };

  const sectionsToRender = () => {
    const sections = [];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const element = document.getElementById(id);
      if (element?.dataset.id) sections.push({ id, section: element.dataset.id, selector: '.js-contents' });
    }
    if (document.querySelector('#CartDrawer')) sections.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' });
    for (const id of ['cart-icon-bubble', 'cart-live-region-text', 'cart-notification-button']) {
      if (sections.length < 5 && document.getElementById(id)) sections.push({ id, section: id, selector: '.shopify-section' });
    }
    return sections;
  };

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
    let data;
    try { data = await response.json(); } catch (error) { throw new Error('Invalid cart response'); }
    if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
  };
  const readCart = async () => {
    const cart = await fetchJSON(`${root}cart.js`);
    if (!Array.isArray(cart.items)) throw new Error('Missing cart items');
    lastCart = cart;
    return cart;
  };
  const mutate = async (action, body) => {
    unsafe = true;
    updateCheckoutLocks();
    const sections = sectionsToRender().map(section => section.section);
    const result = await fetchJSON(`${root}cart/${action}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname })
    });
    mutationRevision++;
    return result;
  };
  const change = async (item, quantity) => {
    if (!isOwned(item) || !itemRule(item) || !item.key) return readCart();
    await mutate('change', { id: item.key, quantity });
    return readCart();
  };

  const removeChargedGifts = async cart => {
    const removalLimit = cart.items.length + 1;
    let removals = 0;
    let ownedCharged = cart.items.find(item => isOwned(item) && itemRule(item) && Number(item.final_line_price) !== 0);
    while (ownedCharged) {
      if (++removals > removalLimit) throw new Error('Gift removal did not update the cart');
      const rule = itemRule(ownedCharged);
      blocked.set(rule.id, cartFingerprint(cart));
      cart = await change(ownedCharged, 0);
      notice = messages.unavailable || '';
      ownedCharged = cart.items.find(item => isOwned(item) && itemRule(item) && Number(item.final_line_price) !== 0);
    }
    unsafe = false;
    return cart;
  };

  const renderSections = async cart => {
    const descriptors = sectionsToRender();
    if (descriptors.length) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', descriptors.map(section => section.section).join(','));
      const sections = await fetchJSON(url.toString());
      const focusId = document.activeElement?.id;
      for (const descriptor of descriptors) {
        if (typeof sections[descriptor.section] !== 'string') continue;
        const html = new DOMParser().parseFromString(sections[descriptor.section], 'text/html');
        const replacement = html.querySelector(descriptor.selector);
        const wrapper = document.getElementById(descriptor.id);
        const target = descriptor.id === 'CartDrawer' ? wrapper : wrapper?.querySelector(descriptor.selector) || wrapper;
        if (replacement && target) target.innerHTML = replacement.innerHTML;
      }
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.item_count === 0);
      document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
      const drawer = document.querySelector('cart-drawer');
      const overlay = drawer?.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', () => drawer.close());
      if (focusId && document.activeElement === document.body) document.getElementById(focusId)?.focus({ preventScroll: true });
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      await publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: cart });
    }
    document.dispatchEvent(new CustomEvent('rg:auto-gift-updated', { detail: { cart, safe: !unsafe } }));
    updateCheckoutLocks();
  };

  const reconcile = async () => {
    const revisionBefore = mutationRevision;
    let cart = await readCart();
    for (const rule of rules) {
      const oversized = cart.items.find(item => isOwned(item) && itemRule(item)?.id === rule.id && Number(item.quantity) > 1);
      if (oversized && rule.available === true && isEligible(rule, cart)) cart = await change(oversized, 1);
    }
    cart = await removeChargedGifts(cart);
    const fingerprint = cartFingerprint(cart);
    let changed = false;
    if (![...blocked.values()].includes(fingerprint)) notice = '';
    for (const rule of rules) {
      const eligible = rule.available === true && isEligible(rule, cart);
      let gifts = cart.items.filter(item => isOwned(item) && itemRule(item)?.id === rule.id);
      if (!eligible) {
        const removalLimit = gifts.length + 1;
        let removals = 0;
        while (gifts.length) {
          if (++removals > removalLimit) throw new Error('Gift removal did not update the cart');
          cart = await change(gifts[0], 0);
          gifts = cart.items.filter(item => isOwned(item) && itemRule(item)?.id === rule.id);
          changed = true;
        }
        continue;
      }
      const duplicateLimit = gifts.length + 1;
      let duplicates = 0;
      while (gifts.length > 1) {
        if (++duplicates > duplicateLimit) throw new Error('Gift removal did not update the cart');
        cart = await change(gifts[1], 0);
        gifts = cart.items.filter(item => isOwned(item) && itemRule(item)?.id === rule.id);
        changed = true;
      }
      gifts = cart.items.filter(item => isOwned(item) && itemRule(item)?.id === rule.id);
      if (gifts[0] && Number(gifts[0].quantity) !== 1) {
        cart = await change(gifts[0], 1);
        changed = true;
      }
      const manual = cart.items.some(item => !isOwned(item) && Number(item.variant_id || item.id) === rule.variantId && Number(item.quantity) > 0);
      if (!gifts.length && !manual && blocked.get(rule.id) !== cartFingerprint(cart)) {
        try {
          await mutate('add', { items: [{ id: rule.variantId, quantity: 1, properties: { _rg_auto_gift: 'true', _rg_auto_gift_rule: rule.id } }] });
          cart = await readCart();
          changed = true;
        } catch (error) {
          blocked.set(rule.id, cartFingerprint(cart));
          cart = await readCart();
          notice = messages.unavailable || '';
        }
      }
      cart = await removeChargedGifts(cart);
    }
    if (changed || mutationRevision > revisionBefore) {
      cart = await readCart();
      cart = await removeChargedGifts(cart);
      await renderSections(cart);
    }
    retryCount = 0;
    window.clearTimeout(retryTimer);
    return cart;
  };

  const settleWaiters = () => {
    for (let index = waiters.length - 1; index >= 0; index--) {
      if (waiters[index].version <= completed) {
        waiters[index].resolve({ cart: lastCart, safe: !unsafe });
        waiters.splice(index, 1);
      }
    }
  };
  const start = () => {
    if (running || externalLocks || completed >= requested) return;
    running = Promise.resolve().then(async () => {
      while (completed < requested && externalLocks === 0) {
        const version = requested;
        try {
          await reconcile();
        } catch (error) {
          notice = unsafe ? messages.unsafe || '' : messages.unavailable || '';
          if (unsafe && retryCount < 3) {
            retryCount++;
            window.clearTimeout(retryTimer);
            retryTimer = window.setTimeout(requestReconcile, retryCount * 1500);
          }
        }
        completed = version;
      }
    }).finally(() => {
      running = null;
      updateCheckoutLocks();
      settleWaiters();
      start();
    });
    updateCheckoutLocks();
  };
  const requestReconcile = () => {
    requested++;
    const pending = new Promise(resolve => waiters.push({ version: requested, resolve }));
    updateCheckoutLocks();
    start();
    return pending;
  };
  const beginCartMutation = () => {
    externalLocks++;
    let releaseReservation;
    const reservation = new Promise(resolve => { releaseReservation = resolve; });
    const ready = Promise.all([externalTail, running || Promise.resolve()]);
    externalTail = ready.then(() => reservation);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      externalLocks--;
      releaseReservation();
      requestReconcile();
    };
    release.ready = ready;
    updateCheckoutLocks();
    return release;
  };

  window.RedgloveAutoGift = { requestReconcile, beginCartMutation, isSafe: () => !unsafe && !locked() };
  const init = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event?.source !== source) requestReconcile();
      });
    }
    document.addEventListener('rg:cart-changed', () => requestReconcile());
    document.addEventListener('click', event => {
      if (!(event.target instanceof Element) || !event.target.closest(checkoutSelector) || !locked()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      notice = unsafe ? messages.unsafe || '' : messages.waiting || '';
      updateCheckoutLocks();
    }, true);
    document.addEventListener('submit', event => {
      if (event.submitter?.name !== 'checkout' || !locked()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      notice = unsafe ? messages.unsafe || '' : messages.waiting || '';
      updateCheckoutLocks();
    }, true);
    if (Number(config.itemCount) > 0 || config.chargedGift) requestReconcile();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
